import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import { toast } from "react-toastify";
import { useGetActivities } from "../../hooks/useGetActivities";
import type { Activity } from "../../hooks/useGetActivities";
import { webRoutes } from "../../srcWeb/router/webRoutes";
import { trackEvent } from "../../mixpanel";
import { TrackingEvents } from "../../srcWeb/Utilities/analytics";
import "./Activities.scss";

type ActivityRoute = string | { path: string; state?: Record<string, unknown> };

const ACTIVITY_ROUTES: Record<string, ActivityRoute> = {
  "Feelings check-in": webRoutes.heartCorner,
  "Breathing Exercise": webRoutes.breathe,
  "Soothing Music":    webRoutes.breatheMusic,
  "Dance Session":     webRoutes.breatheDance,
  "Journal":           webRoutes.journal,
  "Videos":            webRoutes.magicBackpack,
  "Audio Stories":     {path: webRoutes.magicBackpack, state: {contentType:'Audio', navigatedFrom: "journeyscreen", apiUrl:'retrieveMagicBackpack/Audio'}},
  "Quests & Games":    webRoutes.questScreen,
  "Doodle":            { path: webRoutes.doodling, state: { isOnboardingDone: true } },
};

const ActivityCard = ({ activity }: { activity: Activity }) => {
  const [animationData, setAnimationData] = useState<object | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(activity.imageUrl)
      .then((res) => res.json())
      .then(setAnimationData)
      .catch(() => setAnimationData(null));
  }, [activity.imageUrl]);

  const handleClick = () => {
    const route = ACTIVITY_ROUTES[activity.title];
    if (route) {
      trackEvent(TrackingEvents.activity_opened, { activity: activity.title });
      if (typeof route === "string") {
        navigate(route);
      } else {
        navigate(route.path, { state: route.state });
      }
    } else {
      toast.info("Coming soon!");
    }
  };

  return (
    <div className="activity-card" onClick={handleClick}>
      <span className="activity-card__title">{activity.title}</span>
      <div className="activity-card__image">
        {animationData && (
          <Lottie animationData={animationData} loop autoplay style={{ width: 200, height: 200 }} />
        )}
      </div>
    </div>
  );
};

const ActivitiesSkeleton = () => (
  <div className="activities__grid">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="activity-card activity-card--skeleton">
        <div className="activity-card__skeleton-title" />
        <div className="activity-card__skeleton-image" />
      </div>
    ))}
  </div>
);

const Activities = () => {
  const { data, loading, error } = useGetActivities();

  return (
    <div className="activities">
      <div className="activities__divider" />

      {loading && <ActivitiesSkeleton />}

      {!loading && error && (
        <p className="activities__error">Failed to load activities.</p>
      )}

      {!loading && !error && data.length > 0 && (
        <div className="activities__grid">
          {data.map((activity) => (
            <ActivityCard key={activity.documentId} activity={activity} />
          ))}
        </div>
      )}

      {!loading && !error && data.length === 0 && (
        <p className="activities__empty">No activities available.</p>
      )}
    </div>
  );
};

export default Activities;
